import Machine from './Machine';

export default class FactoryMachineManager {
    private machinesSet: { [key: string]: Machine } = {};
    private machines: Machine[] = [];
    private selectedMachine: string | null = null;

    public createMachine(machineId: string, positions: { x: number, y: number, z: number }) {
        const machine = new Machine();
        // Note: positions.z represents the depth/forward-back axis
        machine.setPosition(positions.x, positions.y + 0.3, -positions.z);
        this.machinesSet[machineId] = machine;
        this.machines.push(machine);
        machine.setLabel(machineId);
    }

    public createMachines(locations: { x: number, y: number, machineId: string, guid?: string, pdp1Guid?: string }[]) {
        for (const loc of locations) {
            // Note: loc.y from the service represents the Z-axis (depth) in 3D space
            this.createMachine(loc.machineId, { x: loc.x, y: 0, z: loc.y });
        }
        return locations;
    }

    public getMachine(machineId: string) {
        return this.machinesSet[machineId];
    }

    public getMachines() {
        return this.machines;
    }

    public getAvailableMachines(): string[] {
        return Object.keys(this.machinesSet);
    }

    public getAllMachineIds(): string[] {
        return Object.keys(this.machinesSet);
    }

    public searchMachine(machineId: string): Machine | undefined {
        return this.machinesSet[machineId];
    }

    public findMachinesByPattern(pattern: string): string[] {
        const lowercasePattern = pattern.toLowerCase();
        return Object.keys(this.machinesSet).filter(machineId =>
            machineId.toLowerCase().includes(lowercasePattern)
        );
    }

    public hasMachine(machineId: string): boolean {
        return machineId in this.machinesSet;
    }

    public getSearchSuggestions(searchTerm: string, maxResults: number = 4): string[] {
        const lowercaseSearch = searchTerm.toLowerCase();
        return Object.keys(this.machinesSet)
            .filter(machineId => machineId.toLowerCase().includes(lowercaseSearch))
            .slice(0, maxResults);
    }

    public getMachineCount(): number {
        return Object.keys(this.machinesSet).length;
    }

    /**
     * Select a machine and handle visual selection state
     */
    public async selectMachine(machineId: string | null): Promise<boolean> {
        // Only wait for machines if we're selecting a specific machine

        if (machineId) {
            // Wait for machines to be loaded (max 60 seconds)
            const timeout = 60000; // 60 seconds timeout
            const startTime = Date.now();

            while (Object.keys(this.machinesSet).length === 0) {
                // Check timeout
                if (Date.now() - startTime > timeout) {
                    console.warn('FactoryMachineManager: Timeout waiting for machines to load');
                    return false;
                }
                // Wait a bit before checking again
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }

        if (this.selectedMachine) {
            const prevMachine = this.getMachine(this.selectedMachine);
            if (prevMachine) {
                prevMachine.setSelected(false);
            }
        }


        this.selectedMachine = machineId;

        if (machineId) {
            // Check if machine exists
            const machine = this.getMachine(machineId);
            if (!machine) {
                console.warn(`FactoryMachineManager: Machine ${machineId} not found for selection`);
                return false;
            }
            console.log(`FactoryMachineManager: Selecting machine ${machineId}`);
            // Select new machine
            machine.setSelected(true);
            return true;
        }

        return true;
    }

    /**
     * Get the currently selected machine ID
     */
    public getSelectedMachine(): string | null {
        return this.selectedMachine;
    }

    /**
     * Clear all machine visual highlights (search and selection)
     */
    public clearAllHighlights(): void {
        this.machines.forEach(machine => {
            machine.setSelected(false);
            machine.setSearchHighlight(false);
        });
        this.selectedMachine = null;
    }

    /**
     * Set search highlight for a specific machine
     */
    public setMachineSearchHighlight(machineId: string, highlighted: boolean): boolean {
        const machine = this.getMachine(machineId);
        if (!machine) {
            console.warn(`FactoryMachineManager: Machine ${machineId} not found for search highlight`);
            return false;
        }

        machine.setSearchHighlight(highlighted);
        return true;
    }

    /**
     * Perform search and select machine with visual feedback
     */
    public async searchAndSelectMachine(machineId: string): Promise<boolean> {

        // Check if machine exists
        const machine = this.searchMachine(machineId);
        if (!machine) {
            console.warn(`FactoryMachineManager: Machine ${machineId} not found`);
            return false;
        }

        // Clear previous highlights
        this.clearAllHighlights();

        // Select the machine
        const success = await this.selectMachine(machineId);

        return success;
    }

    /**
     * Get smart search suggestions with prioritization
     */
    public getSmartSearchSuggestions(searchTerm: string, maxResults: number = 4): string[] {
        if (!searchTerm || searchTerm.length === 0) {
            return [];
        }

        const lowercaseSearch = searchTerm.toLowerCase();
        const machineIds = Object.keys(this.machinesSet);

        // Prioritize exact matches and starts-with matches
        const exactMatches: string[] = [];
        const startsWithMatches: string[] = [];
        const containsMatches: string[] = [];

        machineIds.forEach(machineId => {
            const lowercaseMachineId = machineId.toLowerCase();
            if (lowercaseMachineId === lowercaseSearch) {
                exactMatches.push(machineId);
            } else if (lowercaseMachineId.startsWith(lowercaseSearch)) {
                startsWithMatches.push(machineId);
            } else if (lowercaseMachineId.includes(lowercaseSearch)) {
                containsMatches.push(machineId);
            }
        });

        // Combine results with priority: exact > starts-with > contains
        const results = [
            ...exactMatches,
            ...startsWithMatches.sort(),
            ...containsMatches.sort()
        ];

        return results.slice(0, maxResults);
    }

    /**
     * Validate if a machine ID exists (useful for search validation)
     */
    public isValidMachineId(machineId: string): boolean {
        return this.hasMachine(machineId);
    }

    /**
     * Get statistics about machines
     */
    public getMachineStats(): {
        totalMachines: number;
        selectedMachine: string | null;
    } {
        return {
            totalMachines: this.getMachineCount(),
            selectedMachine: this.selectedMachine
        };
    }

    /**
     * Reset all machine states (useful for cleanup)
     */
    public reset(): void {
        this.clearAllHighlights();
        this.selectedMachine = null;
    }

    /**
     * Get machine information for debugging/monitoring
     */
    public getMachineInfo(machineId: string): {
        exists: boolean;
        isSelected: boolean;
        position?: { x: number, y: number, z: number };
    } | null {
        const machine = this.getMachine(machineId);
        if (!machine) {
            return null;
        }

        return {
            exists: true,
            isSelected: this.selectedMachine === machineId,
            position: machine.getPosition()
        };
    }

    /**
     * Search for machines using multiple criteria
     */
    public advancedSearch(criteria: {
        searchTerm?: string;
        exactMatch?: boolean;
        maxResults?: number;
    }): string[] {
        const { searchTerm = '', exactMatch = false, maxResults = 4 } = criteria;

        if (!searchTerm) {
            return Object.keys(this.machinesSet).slice(0, maxResults);
        }

        if (exactMatch) {
            const exactResult = this.getMachine(searchTerm);
            return exactResult ? [searchTerm] : [];
        }

        return this.getSmartSearchSuggestions(searchTerm, maxResults);
    }
}